// Family registration — invite-only flow.
//
// The user arrives here already authenticated via a Supabase admin invite
// magic-link. We collect:
//
//   1. Parent password — required, used for future logins (the magic link is
//      single-use; without a password they'd be locked out next visit).
//   2. Child intake — name, gender, Klasse, ratings, interests, notes.
//   3. Optional 4-digit kid PIN.
//   4. GDPR consent.
//
// On submit we call ``supabase.auth.updateUser({ password })`` then
// ``POST /auth/v3/complete-registration`` which creates the family row and
// the first child profile.
//
// There is no direct-signup path anymore: signups are disabled in the
// Supabase Dashboard for the pilot cohort.

import { useState } from 'react';
import Logo from '../components/brand/Logo';

import { supabase } from '../api/supabase';
import { completeRegistration } from '../api/client';
import type { StudentProfile } from '../api/types';

interface Props {
  onBack: () => void;
  /** Always set in the new flow — the user is invited and signed in via
   *  Supabase but their family record doesn't exist yet. */
  completeMode: {
    email: string;
    onDone: (profile: StudentProfile) => void;
  };
}

type Step = 'password' | 'intake' | 'pin';

const MATHE_OPTIONS = [
  { value: 'below_average', label: 'Unterdurchschnittlich' },
  { value: 'average', label: 'Durchschnittlich' },
  { value: 'above_average', label: 'Überdurchschnittlich' },
] as const;

const ENGAGEMENT_OPTIONS = [
  { value: 'competitive', label: 'Wettbewerbsorientiert' },
  { value: 'collaborative', label: 'Kooperativ' },
  { value: 'needs_encouragement', label: 'Braucht Ermutigung' },
  { value: 'independent', label: 'Unabhängig' },
] as const;

export default function RegisterScreen({ onBack, completeMode }: Props) {
  const [step, setStep] = useState<Step>('password');

  // Credentials — email is fixed (from the invite), only password is set here.
  const [email] = useState(completeMode.email);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [gdprConsent, setGdprConsent] = useState(false);

  // Intake
  const [name, setName] = useState('');
  const [gender, setGender] = useState<'m' | 'f' | 'd'>('f');
  const [klasse, setKlasse] = useState<1 | 2 | 3 | 4>(2);
  const [matheRating, setMatheRating] = useState<'below_average' | 'average' | 'above_average'>('average');
  const [strengths, setStrengths] = useState('');
  const [struggles, setStruggles] = useState('');
  const [engagement, setEngagement] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [interestsRaw, setInterestsRaw] = useState('');

  // Kid PIN
  const [kidPinEnabled, setKidPinEnabled] = useState(true);
  const [kidPin, setKidPin] = useState('');
  const [kidPinConfirm, setKidPinConfirm] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const toggleEngagement = (v: string) =>
    setEngagement((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const submit = async () => {
    if (kidPinEnabled && kidPin.length !== 4) { setError('PIN muss 4 Ziffern haben.'); return; }
    if (kidPinEnabled && kidPin !== kidPinConfirm) { setError('PINs stimmen nicht überein.'); return; }
    if (!gdprConsent) { setError('Bitte den Datenschutzhinweis bestätigen.'); return; }
    setError(null);
    setLoading(true);
    try {
      // Password is set when leaving step 1 (so a mid-wizard bail still
      // leaves a usable login). Here we just create the family + first child.
      const interests = interestsRaw.split(/[,\n]/).map((t) => t.trim()).filter(Boolean).slice(0, 5);
      const intake = {
        name: name.trim(),
        gender,
        klasse,
        mathematik_rating: matheRating,
        known_strengths: strengths.trim(),
        known_struggles: struggles.trim(),
        engagement_style: engagement,
        special_notes: notes.trim(),
        interests,
      };
      const { profile } = await completeRegistration({
        intake,
        kid_pin: kidPinEnabled ? kidPin : null,
        gdpr_consent: gdprConsent,
      });
      completeMode.onDone(profile);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  // Within-wizard back: step 3 → 2 → 1 → out (signOut). Without this the
  // single back arrow at the top blows away the entire session if a user
  // taps it on step 2 or 3, and they have no password yet (it's only set on
  // final submit) — leaving them locked out without a password reset.
  const handleBack = () => {
    if (step === 'pin') { setStep('intake'); return; }
    if (step === 'intake') { setStep('password'); return; }
    onBack();
  };

  // ── Main stepper ──────────────────────────────────────────────────────
  return (
    <Shell>
      <BackButton onBack={handleBack} />
      <div>
        <div style={{ fontFamily: 'var(--display)', fontSize: 36, fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.02em' }}>
          Konto fertigstellen
        </div>
        <div style={{ fontFamily: 'var(--serif)', fontSize: 16, fontStyle: 'italic', color: 'var(--ink-muted)', marginTop: 6 }}>
          Du bist als <strong>{email}</strong> eingeloggt. In drei kurzen Schritten geht es los.
        </div>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 8 }}>
        {(['password', 'intake', 'pin'] as Step[]).map((s, i) => (
          <div key={s} style={{
            flex: 1, height: 3, borderRadius: 2,
            background: s === step ? 'var(--amber)'
              : (['password', 'intake', 'pin'] as Step[]).indexOf(step) > i ? 'var(--ink)'
              : 'rgba(60,50,40,0.15)',
            transition: 'background 0.2s',
          }} />
        ))}
      </div>

      {error && <ErrorBox text={error} />}

      {/* ── Step 1: password setup + GDPR ── */}
      {step === 'password' && (
        <div style={colStyle}>
          <p style={bodyStyle}>
            Wähle ein Passwort, mit dem du dich später wieder einloggen kannst.
            Der Einladungslink funktioniert nur einmal.
          </p>
          <div>
            <label style={labelStyle} htmlFor="reg-pw">Passwort</label>
            <input id="reg-pw" type="password" autoComplete="new-password" style={inputStyle}
              value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Mindestens 8 Zeichen" autoFocus />
          </div>
          <div>
            <label style={labelStyle} htmlFor="reg-pw-confirm">Passwort wiederholen</label>
            <input id="reg-pw-confirm" type="password" autoComplete="new-password" style={inputStyle}
              value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} />
          </div>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="checkbox" checked={gdprConsent}
              onChange={(e) => setGdprConsent(e.target.checked)} style={{ marginTop: 2 }} />
            <span style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
              Ich habe die <a href="/faq.html#datenschutz" target="_blank" rel="noreferrer">Datenschutz&shy;hinweise</a> gelesen
              und willige als Erziehungs&shy;berechtigte*r in die Verarbeitung der Daten meines Kindes ein (DSGVO Art. 8),
              einschließlich der Anfragen an den KI-Anbieter Anthropic in den USA.
            </span>
          </label>
          <PrimaryButton label={loading ? 'Speichere…' : 'Weiter →'}
            disabled={loading || password.length < 8 || password !== passwordConfirm || !gdprConsent}
            onClick={async () => {
              if (password.length < 8) { setError('Passwort muss mindestens 8 Zeichen haben.'); return; }
              if (password !== passwordConfirm) { setError('Passwörter stimmen nicht überein.'); return; }
              setError(null);
              setLoading(true);
              try {
                // Persist the password immediately so a mid-wizard bail
                // doesn't leave the user locked out without a password reset.
                const { error: pwErr } = await supabase.auth.updateUser({ password });
                if (pwErr) {
                  // Supabase rejects identical-to-current passwords with 422 +
                  // "same as the existing password". This happens when the
                  // user reached this screen via the password-reset flow and
                  // typed the same password again. Treat as success — they
                  // already have a usable password.
                  const status = (pwErr as { status?: number }).status;
                  const msg = (pwErr.message || '').toLowerCase();
                  const sameAsOld = status === 422 && (msg.includes('same') || msg.includes('different'));
                  if (!sameAsOld) {
                    setError(pwErr.message);
                    return;
                  }
                }
                setStep('intake');
              } finally {
                setLoading(false);
              }
            }} />
        </div>
      )}

      {/* ── Step 2: intake ── */}
      {step === 'intake' && (
        <div style={colStyle}>
          <div>
            <label style={labelStyle} htmlFor="reg-name">Name des Kindes</label>
            <input id="reg-name" style={inputStyle} value={name}
              onChange={(e) => setName(e.target.value)} placeholder="z.B. Emma" autoFocus />
          </div>

          <div>
            <label style={labelStyle}>Geschlecht</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['f', 'm', 'd'] as const).map((v) => (
                <button key={v} type="button" onClick={() => setGender(v)}
                  style={{ flex: 1, padding: '10px', background: gender === v ? 'var(--amber-soft)' : 'var(--card)',
                    border: `1.5px solid ${gender === v ? 'var(--amber)' : 'rgba(60,50,40,0.18)'}`,
                    borderRadius: 4, fontFamily: 'var(--sans)', fontSize: 14, cursor: 'pointer', color: 'var(--ink)' }}>
                  {v === 'f' ? 'Mädchen' : v === 'm' ? 'Junge' : 'divers'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={labelStyle}>Klasse</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {([1, 2, 3, 4] as const).map((k) => (
                <button key={k} type="button" onClick={() => setKlasse(k)}
                  style={{ flex: 1, padding: '10px', background: klasse === k ? 'var(--amber-soft)' : 'var(--card)',
                    border: `1.5px solid ${klasse === k ? 'var(--amber)' : 'rgba(60,50,40,0.18)'}`,
                    borderRadius: 4, fontFamily: 'var(--display)', fontSize: 18, fontWeight: 600, cursor: 'pointer', color: 'var(--ink)' }}>
                  {k}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={labelStyle}>Mathematik — Einschätzung</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {MATHE_OPTIONS.map((opt) => (
                <button key={opt.value} type="button" onClick={() => setMatheRating(opt.value)}
                  style={{ textAlign: 'left', padding: '10px 14px',
                    background: matheRating === opt.value ? 'var(--amber-soft)' : 'var(--card)',
                    border: `1.5px solid ${matheRating === opt.value ? 'var(--amber)' : 'rgba(60,50,40,0.18)'}`,
                    borderRadius: 4, fontFamily: 'var(--sans)', fontSize: 14, cursor: 'pointer', color: 'var(--ink)' }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={labelStyle} htmlFor="reg-interests">Interessen (kommasepariert, max. 5)</label>
            <input id="reg-interests" style={inputStyle} value={interestsRaw}
              onChange={(e) => setInterestsRaw(e.target.value)}
              placeholder="z.B. Dinosaurier, Pferde, Kochen" />
          </div>

          <button type="button" onClick={() => setShowMore((s) => !s)}
            style={{ background: 'none', border: 'none', fontFamily: 'var(--mono)', fontSize: 10,
              letterSpacing: '0.15em', color: 'var(--ink-muted)', textTransform: 'uppercase',
              cursor: 'pointer', textAlign: 'left', padding: 0 }}>
            {showMore ? '– Weniger anzeigen' : '+ Mehr Felder (optional)'}
          </button>

          {showMore && (
            <>
              <div>
                <label style={labelStyle} htmlFor="reg-strengths">Stärken</label>
                <textarea id="reg-strengths" style={textareaStyle} value={strengths}
                  onChange={(e) => setStrengths(e.target.value)}
                  placeholder="Was kann dein Kind besonders gut?" />
              </div>
              <div>
                <label style={labelStyle} htmlFor="reg-struggles">Schwierigkeiten</label>
                <textarea id="reg-struggles" style={textareaStyle} value={struggles}
                  onChange={(e) => setStruggles(e.target.value)}
                  placeholder="Wobei braucht dein Kind Unterstützung?" />
              </div>
              <div>
                <label style={labelStyle}>Lern-Stil</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {ENGAGEMENT_OPTIONS.map((opt) => (
                    <button key={opt.value} type="button" onClick={() => toggleEngagement(opt.value)}
                      style={{ padding: '8px 14px',
                        background: engagement.includes(opt.value) ? 'var(--amber-soft)' : 'var(--card)',
                        border: `1.5px solid ${engagement.includes(opt.value) ? 'var(--amber)' : 'rgba(60,50,40,0.18)'}`,
                        borderRadius: 4, fontFamily: 'var(--sans)', fontSize: 13, cursor: 'pointer', color: 'var(--ink)' }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={labelStyle} htmlFor="reg-notes">Besondere Hinweise</label>
                <textarea id="reg-notes" style={textareaStyle} value={notes}
                  onChange={(e) => setNotes(e.target.value)} />
              </div>
            </>
          )}

          <PrimaryButton label="Weiter →" disabled={!name.trim()}
            onClick={() => { setError(null); setStep('pin'); }} />
        </div>
      )}

      {/* ── Step 3: kid PIN ── */}
      {step === 'pin' && (
        <div style={colStyle}>
          <p style={bodyStyle}>
            Ein kurzer PIN hilft deinem Kind, sich auf einem gemeinsamen Gerät selbst einzuloggen.
            Für einen Familien-iPad zuhause kannst du ihn auch weglassen.
          </p>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={kidPinEnabled}
              onChange={(e) => setKidPinEnabled(e.target.checked)} />
            <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
              PIN für mein Kind verwenden
            </span>
          </label>
          {kidPinEnabled && (
            <>
              <div>
                <label style={labelStyle} htmlFor="reg-kid-pin">PIN (4 Ziffern)</label>
                <input id="reg-kid-pin" style={inputStyle} inputMode="numeric" maxLength={4}
                  value={kidPin} onChange={(e) => setKidPin(e.target.value.replace(/[^0-9]/g, ''))}
                  autoFocus />
              </div>
              <div>
                <label style={labelStyle} htmlFor="reg-kid-pin-confirm">PIN wiederholen</label>
                <input id="reg-kid-pin-confirm" style={inputStyle} inputMode="numeric" maxLength={4}
                  value={kidPinConfirm}
                  onChange={(e) => setKidPinConfirm(e.target.value.replace(/[^0-9]/g, ''))} />
              </div>
            </>
          )}
          <PrimaryButton label={loading ? 'Speichere…' : 'Registrieren'} disabled={loading} onClick={submit} />
        </div>
      )}
    </Shell>
  );
}

// ── Presentational helpers ───────────────────────────────────────────────
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', minHeight: '100dvh', alignItems: 'flex-start',
      justifyContent: 'center', background: 'var(--parchment)',
      fontFamily: 'var(--sans)', overflowY: 'auto', padding: '48px 24px' }}>
      <div style={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
          <Logo variant="lockup" height={96} />
        </div>
        {children}
      </div>
    </div>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <button onClick={onBack} style={{ background: 'none', border: 'none',
      fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.15em',
      color: 'var(--ink-muted)', textTransform: 'uppercase', cursor: 'pointer',
      textAlign: 'left', padding: 0 }}>
      ← Zurück
    </button>
  );
}

function PrimaryButton({ label, disabled, onClick }: { label: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ width: '100%', padding: '14px',
        background: disabled ? 'rgba(60,50,40,0.1)' : 'var(--amber)',
        color: disabled ? 'var(--ink-muted)' : '#fff',
        border: 'none', borderRadius: 3, fontFamily: 'var(--sans)',
        fontSize: 16, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
        boxShadow: disabled ? 'none' : '0 4px 14px rgba(201,106,47,0.35)',
        transition: 'all 0.15s' }}>
      {label}
    </button>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div style={{ padding: '10px 14px', background: 'var(--wrong-soft)',
      border: '1px solid var(--wrong)', borderRadius: 3, fontFamily: 'var(--mono)',
      fontSize: 11, color: 'var(--wrong)' }}>
      {text}
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────
const labelStyle: React.CSSProperties = { fontFamily: 'var(--mono)', fontSize: 10,
  letterSpacing: '0.18em', color: 'var(--ink-muted)', textTransform: 'uppercase',
  display: 'block', marginBottom: 6 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 14px',
  background: 'var(--card)', border: '1px solid rgba(60,50,40,0.2)', borderRadius: 3,
  fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--ink)', boxSizing: 'border-box' };
const textareaStyle: React.CSSProperties = { ...inputStyle, minHeight: 72, resize: 'vertical' as const };
const colStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16 };
const bodyStyle: React.CSSProperties = { fontFamily: 'var(--serif)', fontSize: 16,
  color: 'var(--ink-soft)', lineHeight: 1.6 };
